import { useEffect, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Armchair, ChefHat, LayoutGrid, Check, Copy, ExternalLink, Globe, Lock, RefreshCw, Store, TriangleAlert } from "lucide-react";
import { parseMoneyToMinor, minorToDecimalString, type DomainCheckDto, type RestaurantDto } from "@dishfolio/contracts";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Field, Input, NativeSelect, Switch, cn, toast } from "@dishfolio/ui";
import { useMenus } from "../lib/queries.ts";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";
import { useMediaPicker } from "../components/MediaPicker.tsx";
import { RestaurantFields, type RestaurantDraft } from "./onboarding.tsx";

export function RestaurantPage() {
  const { restaurantId } = useParams({ from: "/app/restaurants/$restaurantId" });
  const { org, can , feature } = useOrg();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["restaurant", restaurantId], queryFn: () => api.get<{ restaurant: RestaurantDto }>(`/restaurants/${restaurantId}`).then((r) => r.restaurant) });
  const r = q.data;
  const [draft, setDraft] = useState<RestaurantDraft | null>(null);
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [tax, setTax] = useState("");
  const [minOrder, setMinOrder] = useState("");
  const picker = useMediaPicker();
  useEffect(() => {
    if (!r) return;
    setDraft({ name: r.name, address: r.address ?? "", defaultLanguage: r.defaultLanguage, currency: r.currency, timezone: r.timezone });
    setPhone(r.phone ?? "");
    setWebsite(r.website ?? "");
    setTax(String(r.taxRateBps / 100));
    setMinOrder(r.minimumOrderMinor ? minorToDecimalString(r.minimumOrderMinor, r.currency) : "");
  }, [r]);
  if (!r || !draft) return null;
  const save = async (patch: Record<string, unknown>) => {
    try {
      await api.patch(`/restaurants/${restaurantId}`, patch);
      await qc.invalidateQueries({ queryKey: ["restaurant", restaurantId] });
      await qc.invalidateQueries({ queryKey: ["restaurants"] });
      toast.success("Saved");
    } catch (err) {
      notifyError(err);
    }
  };
  const editable = can("restaurant:manage");
  return (
    <PageContainer>
      <PageHeader eyebrow="Location" title={r.name} actions={org?.plan.tableOrdering ? <>{feature("table_management") ? <Button asChild variant="outline"><Link to="/restaurants/$restaurantId/floor" params={{ restaurantId }}><LayoutGrid /> Floor plan</Link></Button> : null}<Button asChild variant="outline"><Link to="/restaurants/$restaurantId/kitchen" params={{ restaurantId }}><ChefHat /> Kitchen</Link></Button><Button asChild variant="outline"><Link to="/restaurants/$restaurantId/tables" params={{ restaurantId }}><Armchair /> Tables & QR</Link></Button></> : null} />
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="size-4" /> Details
            </CardTitle>
            <CardDescription>Shown on your menus. Currency and time zone are used for prices, ordering and analytics.</CardDescription>
          </CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex size-20 items-center justify-center overflow-hidden rounded-xl border bg-muted">{r.logoUrl ? <img src={r.logoUrl} alt="Logo" className="size-full object-contain" /> : <span className="text-xs text-muted-foreground">No logo</span>}</div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!editable}
                onClick={async () => {
                  const p = await picker.pick({ multiple: false });
                  if (p?.[0]) await save({ logoAssetId: p[0].id });
                }}
              >
                {r.logoUrl ? "Change logo" : "Upload logo"}
              </Button>
              {r.logoUrl ? (
                <Button variant="ghost" size="sm" disabled={!editable} onClick={() => save({ logoAssetId: null })}>
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
          <RestaurantFields value={draft} onChange={setDraft} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Phone">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="Website">
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
            </Field>
          </div>
          <Button className="justify-self-start" disabled={!editable} onClick={() => save({ ...draft, phone, website: website && !/^https?:\/\//.test(website) ? `https://${website}` : website })}>
            Save details
          </Button>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Tax</CardTitle>
            <CardDescription>Used to show the tax share on table orders. Item-level overrides are available in the item editor.</CardDescription>
          </CardHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tax rate (%)">
              <Input inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} />
            </Field>
            <label className="flex items-center justify-between gap-3 self-end text-sm">
              <span>Menu prices include tax</span>
              <Switch checked={r.taxInclusive} disabled={!editable} onCheckedChange={(v) => save({ taxInclusive: v })} />
            </label>
          </div>
          <Button className="justify-self-start" variant="outline" disabled={!editable} onClick={() => save({ taxRateBps: Math.round(Number(tax.replace(",", ".")) * 100) || 0 })}>
            Save tax
          </Button>
        </Card>
        {org?.plan.tableOrdering ? (
          <Card>
            <CardHeader>
              <CardTitle>Table ordering</CardTitle>
              <CardDescription>Guests order from their phone; payment stays at the table or counter.</CardDescription>
            </CardHeader>
            <label className="flex items-center justify-between text-sm">
              <span>Accept table orders</span>
              <Switch checked={r.orderingEnabled} disabled={!editable} onCheckedChange={(v) => save({ orderingEnabled: v })} />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span>
                Pause ordering now
                <span className="block text-xs text-muted-foreground">For a rush or kitchen closing — guests can still browse.</span>
              </span>
              <Switch checked={r.orderingPaused} disabled={!can("orders:manage")} onCheckedChange={(v) => save({ orderingPaused: v })} />
            </label>
            <div className="flex items-end gap-2">
              <Field label={`Minimum order (${r.currency})`} className="max-w-xs">
                <Input inputMode="decimal" value={minOrder} onChange={(e) => setMinOrder(e.target.value)} placeholder="0.00" />
              </Field>
              <Button variant="outline" disabled={!editable} onClick={() => save({ minimumOrderMinor: minOrder.trim() ? (parseMoneyToMinor(minOrder, r.currency) ?? 0) : 0 })}>
                Save
              </Button>
            </div>
          </Card>
        ) : (
          <UpgradeCard plan="Business" title="Table ordering" description="Table-specific QR codes, a live order board for staff, and order analytics." />
        )}
        <CustomDomainCard restaurant={r} editable={editable} onSave={save} />
      </div>
      {picker.element}
    </PageContainer>
  );
}

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 font-mono text-xs hover:bg-muted"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      aria-label={`Copy ${value}`}
    >
      {value} {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3 text-muted-foreground" />}
    </button>
  );
}

function StatusLine({ ok, warn, children }: { ok: boolean; warn?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className={cn("mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full", ok ? "bg-success/15 text-success" : warn ? "bg-warning/25 text-foreground" : "bg-muted text-muted-foreground")}>
        {ok ? <Check className="size-3" /> : <TriangleAlert className="size-2.5" />}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** Connect a domain the restaurant owns: choose the host and menu, add one DNS record, verify. */
function CustomDomainCard({ restaurant: r, editable, onSave }: { restaurant: RestaurantDto; editable: boolean; onSave: (patch: Record<string, unknown>) => Promise<void> }) {
  const { org } = useOrg();
  const menus = useMenus();
  const [domain, setDomain] = useState(r.customDomain ?? "");
  useEffect(() => setDomain(r.customDomain ?? ""), [r.customDomain]);
  const check = useQuery({
    queryKey: ["domain-check", r.id, r.customDomain],
    queryFn: () => api.get<DomainCheckDto>(`/restaurants/${r.id}/domain-check`),
    enabled: Boolean(r.customDomain && org?.plan.customDomain),
    refetchInterval: (q) => (q.state.data && q.state.data.dns.status === "ok" && q.state.data.https === "ok" ? false : 30_000),
  });
  const locationMenus = (menus.data ?? []).filter((m) => m.restaurantId === r.id);
  const published = locationMenus.filter((m) => m.status === "published");
  const apex = (r.customDomain ?? domain).split(".").length === 2;
  const c = check.data;
  const live = c?.dns.status === "ok" && c.https === "ok";
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="size-4" /> Custom domain {live ? <Badge variant="success">Live</Badge> : r.customDomain ? <Badge variant="warning">Setting up</Badge> : null}
        </CardTitle>
        <CardDescription>Serve a menu at your own address, e.g. menu.your-restaurant.com, with HTTPS included.</CardDescription>
      </CardHeader>
      {!org?.plan.customDomain ? (
        <UpgradeCard plan="Business" title="Your own domain" description="Show your menu at menu.your-restaurant.com instead of a dishfolio.app link. HTTPS certificates are issued and renewed automatically." />
      ) : (
        <div className="grid gap-6">
          <div className="grid gap-3">
            <div className="text-sm font-medium">1. Choose the address</div>
            <div className="flex flex-wrap gap-2">
              <Input className="min-w-56 flex-1" value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase().trim().replace(/^https?:\/\//, "").replace(/\/.*$/, ""))} placeholder="menu.your-restaurant.com" disabled={!editable} />
              <Button variant="outline" disabled={!editable || domain === (r.customDomain ?? "")} onClick={() => onSave({ customDomain: domain || null })}>
                {domain || !r.customDomain ? "Save" : "Disconnect"}
              </Button>
            </div>
            <Field label="Menu shown on this domain" hint={published.length === 0 ? "Publish a menu for this location first." : undefined}>
              <NativeSelect value={r.domainMenuId ?? ""} disabled={!editable || !r.customDomain} onChange={(e) => onSave({ domainMenuId: e.target.value || null })}>
                <option value="">{published[0] ? `Automatic (${published[0].name})` : "Automatic (first published menu)"}</option>
                {locationMenus.map((m) => (
                  <option key={m.id} value={m.id} disabled={m.status !== "published"}>
                    {m.name}
                    {m.status !== "published" ? " (not published)" : ""}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          {r.customDomain ? (
            <>
              <div className="grid gap-3">
                <div className="text-sm font-medium">2. Add this DNS record where you bought the domain</div>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium">Name / Host</th>
                        <th className="px-3 py-2 font-medium">Value / Points to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apex ? (
                        (c?.targetIps.length ? c.targetIps : ["…"]).map((ip) => (
                          <tr key={ip} className="border-t">
                            <td className="px-3 py-2 font-mono text-xs">A</td>
                            <td className="px-3 py-2">
                              <CopyValue value="@" />
                            </td>
                            <td className="px-3 py-2">{ip === "…" ? "…" : <CopyValue value={ip} />}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">CNAME</td>
                          <td className="px-3 py-2">
                            <CopyValue value={r.customDomain.split(".").slice(0, -2).join(".")} />
                          </td>
                          <td className="px-3 py-2">
                            <CopyValue value={c?.target ?? "menus.dishfolio.app"} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  {apex
                    ? "Root domains (without www or menu.) need an A record. A subdomain like menu.your-restaurant.com is easier: it only needs one CNAME record."
                    : "Some providers want the full name (" + r.customDomain + ") instead. Remove any other A or CNAME records for this name. Changes usually apply within minutes, sometimes up to a few hours."}
                </p>
              </div>
              <div className="grid gap-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">3. We check it for you</div>
                  <Button variant="ghost" size="sm" onClick={() => void check.refetch()} loading={check.isFetching}>
                    <RefreshCw /> Check now
                  </Button>
                </div>
                {!c ? (
                  <p className="text-sm text-muted-foreground">Checking…</p>
                ) : (
                  <div className="grid gap-2">
                    <StatusLine ok={c.dns.status === "ok"} warn={c.dns.status === "wrong"}>
                      {c.dns.status === "ok"
                        ? "DNS points to Dishfolio."
                        : c.dns.status === "missing"
                          ? "No DNS record found yet."
                          : `DNS points somewhere else (${c.dns.found.join(", ")}).`}
                    </StatusLine>
                    <StatusLine ok={c.https === "ok"}>{c.https === "ok" ? "HTTPS certificate is active." : c.dns.status === "ok" ? "Issuing the HTTPS certificate. This usually takes under a minute." : "HTTPS starts automatically once DNS is correct."}</StatusLine>
                    {live ? (
                      <a href={c.url} target="_blank" rel="noopener" className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                        Open {c.domain} <ExternalLink className="size-3.5" />
                      </a>
                    ) : null}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}
